import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { debounceTime, switchMap, catchError, map } from 'rxjs/operators';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { SubdivisionService } from '../subdivision.service';
import { MessagerComponent } from '../../messager/messager/messager.component';

export interface MfContactsDialogData {
  subdivision_id: number;
  subdivision_name: string;
  customer_id: number;
  customer_name: string;
}

export interface MfContactRow {
  contact_id: number;
  name: string;
  email: string;
  phone: string;
  customer_name: string;
  contact_type: string;
  rank: string;
  missing?: boolean;
  inactive?: boolean;
}

@Component({
  selector: 'app-mf-contacts-dialog',
  templateUrl: 'mf-contacts-dialog.html'
})
export class MfContactsDialogComponent implements OnInit {
  faTrashAlt = faTrashAlt;
  rows: MfContactRow[] = [];
  types: string[] = [];
  ranks: string[] = [];
  loading = true;
  saving = false;
  isBillingBldg = false;
  searchControl = new FormControl('');
  builderOnly = new FormControl(false);
  search$: Observable<any[]>;

  constructor(public dialogRef: MatDialogRef<MfContactsDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: MfContactsDialogData,
              private subdivisionService: SubdivisionService,
              private messager: MessagerComponent) {}

  ngOnInit(): void {
    this.subdivisionService.getMfContacts(this.data.subdivision_id).subscribe(
      res => {
        this.loading = false;
        if (res.Error) {
          this.messager.openSnackBar(res.Message, '[close]', 'errorAlert');
          return;
        }
        this.types = res.Types || [];
        this.ranks = res.Ranks || [];
        this.isBillingBldg = !!res.IsBillingBldg;
        this.rows = (res.Contacts || []).map(c => ({
          contact_id: c.contact_id, name: c.name, email: c.email, phone: c.phone,
          customer_name: c.customer_name, contact_type: c.contact_type, rank: c.rank || '',
          missing: !!c.missing, inactive: !!c.inactive
        }));
      },
      err => {
        this.loading = false;
        this.messager.openSnackBar('Could not load MF contacts', '[close]', 'errorAlert');
      }
    );

    this.search$ = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        const term = typeof value === 'string' ? value.trim() : '';
        if (term.length < 2) { return of([]); }
        const customer = this.builderOnly.value ? this.data.customer_id : undefined;
        return this.subdivisionService.searchMfContacts(term, customer).pipe(
          map(res => Array.isArray(res) ? res : []),
          catchError(() => of([]))
        );
      })
    );

    // Re-run the current search when the builder filter flips.
    this.builderOnly.valueChanges.subscribe(() => {
      this.searchControl.setValue(this.searchControl.value || '');
    });
  }

  displayBlank(): string { return ''; }

  addContact(contact: any): void {
    if (!contact || !contact.id) { return; }
    // One untyped row per contact at a time; the same contact can be added
    // again with a different type once this row has one.
    const pending = this.rows.find(r => r.contact_id === contact.id && !r.contact_type);
    if (pending) {
      this.messager.openSnackBar('Pick a type for ' + contact.name + ' first', '[close]', 'errorAlert');
      this.searchControl.setValue('');
      return;
    }
    this.rows.push({
      contact_id: contact.id, name: contact.name, email: contact.email || '',
      phone: contact.phone || '', customer_name: contact.customer_name || '', contact_type: '', rank: ''
    });
    this.searchControl.setValue('');
  }

  setType(row: MfContactRow, type: string): void {
    row.contact_type = type;
  }

  setRank(row: MfContactRow, rank: string): void {
    row.rank = rank || '';
  }

  removeRow(index: number): void {
    this.rows.splice(index, 1);
  }

  isDuplicate(row: MfContactRow): boolean {
    if (!row.contact_type) { return false; }
    return this.rows.filter(r => r.contact_id === row.contact_id && r.contact_type === row.contact_type).length > 1;
  }

  get untypedCount(): number {
    return this.rows.filter(r => !r.contact_type).length;
  }

  get duplicateCount(): number {
    return this.rows.filter(r => this.isDuplicate(r)).length;
  }

  get canSave(): boolean {
    return !this.loading && !this.saving && this.untypedCount === 0 && this.duplicateCount === 0;
  }

  save(): void {
    if (!this.canSave) { return; }
    this.saving = true;
    const payload = this.rows.map(r => ({
      contact_id: r.contact_id, name: r.name, contact_type: r.contact_type, rank: r.rank || null
    }));
    this.subdivisionService.saveMfContacts(this.data.subdivision_id, payload).subscribe(
      res => {
        this.saving = false;
        if (res.Error) {
          this.messager.openSnackBar(res.Message, '[close]', 'errorAlert');
          return;
        }
        const sync = res.PostSync;
        const note = sync && (sync.updated || sync.created) ? ' Extra Notes contact post updated.' : '';
        this.messager.openSnackBar('MF contacts saved.' + note, '[close]', 'successAlert', 4000);
        this.dialogRef.close(res.Contacts || payload);
      },
      err => {
        this.saving = false;
        this.messager.openSnackBar('Could not save MF contacts', '[close]', 'errorAlert');
      }
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
